import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { uploadCsvForMatching, matchMoxfieldTekst, getBuylistImageUrl } from '../services/buylistService';
import { addToBuylist, addBulkToBuylist } from '../redux/buylistSlice';
import { BuylistItem } from '../types';
import { trackAddToCart } from '../utils/metaPixel';
import BuylistLayout from '../components/buylist/BuylistLayout';
import { theme, media } from '../styles/theme';
import {
  PageContainer as BasePageContainer,
  PageTitle as BasePageTitle,
  Card,
  Text,
  Button,
  Alert,
  FormGroup,
  Label
} from '../styles/components';

// ======== Styled Components ========

const PageContainer = styled(BasePageContainer)``;

const PageTitle = styled(BasePageTitle)``;

const UploadCard = styled(Card)`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  border-color: rgba(255, 255, 255, 0.1);
  box-sizing: border-box;
`;

const SourceLabel = styled(Label)`
  margin-bottom: ${theme.spacing.sm};
`;

const SourceOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.md};
`;

const SourceOption = styled.label<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${props => props.isSelected ? theme.colors.accent : 'rgba(255, 255, 255, 0.2)'};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: border-color ${theme.transitions.fast}, background-color ${theme.transitions.fast};
  background-color: ${props => props.isSelected ? 'rgba(212, 74, 60, 0.2)' : 'transparent'};
  font-weight: ${props => props.isSelected ? theme.fontWeights.semibold : theme.fontWeights.regular};
  color: ${theme.colors.white};
  flex: 1;
  justify-content: center;
  min-width: 0;

  &:hover {
    background-color: ${props => props.isSelected ? 'rgba(212, 74, 60, 0.2)' : 'rgba(255, 255, 255, 0.08)'};
  }

  /* Tablet: keep all three in a row */
  ${media.tablet} {
    padding: ${theme.spacing.sm};
  }

  /* Mobile: each option takes full width and stacks vertically */
  ${media.mobile} {
    flex: 0 0 100%;
    justify-content: flex-start;
    padding: 0.75rem ${theme.spacing.sm};
  }
`;

const RadioInput = styled.input`
  margin-right: ${theme.spacing.xs};
`;

const MoxfieldTextarea = styled.textarea`
  width: 100%;
  min-height: 180px;
  padding: ${theme.spacing.sm};
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.white};
  font-family: monospace;
  font-size: ${theme.fontSizes.sm};
  resize: vertical;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
  }
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.white};

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileName = styled.span`
  display: block;
  margin-top: ${theme.spacing.xs};
  font-size: ${theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.7);
`;

const ResultsSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};

  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ResultsCount = styled.span`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-bottom: ${theme.spacing.md};
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${theme.colors.gray.light};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.card};
`;

const THead = styled.thead`
  background-color: ${theme.colors.gray.lightest};

  th {
    padding: ${theme.spacing.sm};
    text-align: left;
    font-weight: ${theme.fontWeights.semibold};
    border-bottom: 2px solid ${theme.colors.gray.light};
    white-space: nowrap;
  }
`;

const TBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${theme.colors.gray.light};

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: ${theme.colors.gray.lightest};
    }
  }

  td {
    padding: ${theme.spacing.sm};
    vertical-align: middle;
  }
`;

const ItemImage = styled.div<{ $imageUrl: string }>`
  width: 60px;
  height: 60px;
  background-image: url(${props => props.$imageUrl});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: ${theme.colors.gray.lightest};
  border-radius: ${theme.borderRadius.sm};
`;

const QuantityInput = styled.input`
  width: 60px;
  padding: ${theme.spacing.xs};
  border: 1px solid ${theme.colors.gray.light};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSizes.md};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const PriceCell = styled.span`
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.success};
  white-space: nowrap;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;

  ${media.mobile} {
    flex-direction: column;

    > * {
      width: 100%;
    }
  }
`;

const SummaryBox = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  box-shadow: ${theme.shadows.card};
  margin-bottom: ${theme.spacing.md};
`;

const SummaryTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.medium};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.white};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes.lg};
    padding-top: 0.75rem;
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: rgba(231, 76, 60, 0.08);
  color: ${theme.colors.danger};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.danger};
    color: ${theme.colors.white};
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(231, 76, 60, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const StoreCreditHighlight = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  margin-top: ${theme.spacing.sm};
  background-color: rgba(45, 184, 107, 0.1);
  border-radius: ${theme.borderRadius.sm};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.success};
`;

// ======== Helper ========

const extractErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && data.length > 0) return data;
    if (data?.message) return data.message;
    if (error.response?.status === 400) return 'Ugyldig filformat eller kilde.';
    if (error.response?.status === 500) return 'En serverfeil oppstod. Vennligst prøv igjen.';
  }
  return 'En uventet feil oppstod. Vennligst prøv igjen.';
};

// ======== Component ========

type CsvSource = 'manabox' | 'deckbox' | 'moxfield';

const MOCK_CSV_ITEMS: BuylistItem[] = [
  { buylistID: 1, cardBlueprintId: 101, maxQuantity: 4, buyPrice: 350.00, buylistStoreCreditPrice: 385.00, foil: false, blueprintName: 'Gaea\'s Cradle', blueprintVersion: '1', expansionName: 'Urza\'s Saga', image_url: '', csvAntall: 2 },
  { buylistID: 2, cardBlueprintId: 102, maxQuantity: 4, buyPrice: 120.50, buylistStoreCreditPrice: 132.55, foil: true, blueprintName: 'Atraxa, Praetors\' Voice', blueprintVersion: '1', expansionName: 'Double Masters', image_url: '', csvAntall: 1 },
  { buylistID: 3, cardBlueprintId: 103, maxQuantity: 2, buyPrice: 75.00, buylistStoreCreditPrice: 82.50, foil: false, blueprintName: 'Doubling Season', blueprintVersion: '1', expansionName: 'Foundations', image_url: '', csvAntall: 2 },
  { buylistID: 4, cardBlueprintId: 104, maxQuantity: 4, buyPrice: 45.00, buylistStoreCreditPrice: 49.50, foil: false, blueprintName: 'Force of Will', blueprintVersion: '1', expansionName: 'Dominaria Remastered', image_url: '', csvAntall: 3 },
  { buylistID: 5, cardBlueprintId: 105, maxQuantity: 4, buyPrice: 15.00, buylistStoreCreditPrice: 16.50, foil: true, blueprintName: 'Korvold, Fae-Cursed King', blueprintVersion: '1', expansionName: 'Throne of Eldraine', image_url: '', csvAntall: 1 },
];

const CsvUploadPage: React.FC = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();

  const [selectedSource, setSelectedSource] = useState<CsvSource | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [moxfieldTekst, setMoxfieldTekst] = useState('');
  const [matchedItems, setMatchedItems] = useState<BuylistItem[]>([]);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && searchParams.get('mock') === 'true') {
      setMatchedItems(MOCK_CSV_ITEMS);
      const initialQuantities: Record<number, number> = {};
      MOCK_CSV_ITEMS.forEach(item => {
        initialQuantities[item.buylistID] = Math.min(item.csvAntall || 1, item.maxQuantity);
      });
      setQuantities(initialQuantities);
      setUploadComplete(true);
    }
  }, [searchParams]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedSource) return;

    setLoading(true);
    setError(null);

    try {
      let items: BuylistItem[];

      if (selectedSource === 'moxfield') {
        if (!moxfieldTekst.trim()) {
          setError('Kortlisten er tom.');
          setLoading(false);
          return;
        }
        items = await matchMoxfieldTekst(moxfieldTekst);
      } else {
        if (!selectedFile) return;

        // Sjekk antall linjer i filen før opplasting
        try {
          const text = await selectedFile.text();
          const lineCount = text.split('\n').filter(line => line.trim().length > 0).length - 1; // -1 for header
          if (lineCount > 2000) {
            setError(`Filen inneholder ${lineCount} rader, men maks 2000 er tillatt. Vennligst reduser antall rader og prøv igjen.`);
            setLoading(false);
            return;
          }
        } catch {
          // Hvis fillesing feiler, la serveren håndtere valideringen
        }

        items = await uploadCsvForMatching(selectedFile, selectedSource);
      }

      setMatchedItems(items);
      // Initialiser antall fra listen, begrenset til maxQuantity
      const initialQuantities: Record<number, number> = {};
      items.forEach(item => {
        initialQuantities[item.buylistID] = Math.min(item.csvAntall || 1, item.maxQuantity);
      });
      setQuantities(initialQuantities);
      setUploadComplete(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (buylistID: number, value: number, maxQuantity: number) => {
    const clamped = Math.max(1, Math.min(value, maxQuantity));
    setQuantities(prev => ({ ...prev, [buylistID]: clamped }));
  };

  const handleAddItem = (item: BuylistItem) => {
    const quantity = quantities[item.buylistID] || 1;
    dispatch(addToBuylist({ item, quantity }));
    trackAddToCart(item.blueprintName, quantity, (item.buyPrice || 0) * quantity);
  };

  const handleAddAll = () => {
    const bulkItems = matchedItems.map(item => ({
      item,
      quantity: quantities[item.buylistID] || 1,
    }));
    dispatch(addBulkToBuylist({ items: bulkItems }));
    const totalValue = bulkItems.reduce((sum, { item, quantity }) => sum + (item.buyPrice || 0) * quantity, 0);
    const totalQty = bulkItems.reduce((sum, { quantity }) => sum + quantity, 0);
    trackAddToCart('CSV Bulk Add', totalQty, totalValue);
  };

  const handleRemoveItem = (buylistID: number) => {
    setMatchedItems(prev => prev.filter(item => item.buylistID !== buylistID));
    setQuantities(prev => {
      const next = { ...prev };
      delete next[buylistID];
      return next;
    });
  };

  const handleReset = () => {
    setSelectedSource(null);
    setSelectedFile(null);
    setMoxfieldTekst('');
    setMatchedItems([]);
    setQuantities({});
    setError(null);
    setUploadComplete(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ---- Upload Form ----
  const renderUploadForm = () => (
    <UploadCard>
      <Text style={{ color: 'white', opacity: 0.8 }}>
        Last opp en CSV-fil fra Manabox eller Deckbox, eller lim inn en kortliste fra Moxfield for å finne kort vi kjøper.
      </Text>

      <FormGroup>
        <SourceLabel>Velg kilde</SourceLabel>
        <SourceOptions>
          <SourceOption isSelected={selectedSource === 'manabox'}>
            <RadioInput
              type="radio"
              name="csvSource"
              checked={selectedSource === 'manabox'}
              onChange={() => setSelectedSource('manabox')}
            />
            Manabox
          </SourceOption>
          <SourceOption isSelected={selectedSource === 'deckbox'}>
            <RadioInput
              type="radio"
              name="csvSource"
              checked={selectedSource === 'deckbox'}
              onChange={() => setSelectedSource('deckbox')}
            />
            Deckbox
          </SourceOption>
          <SourceOption isSelected={selectedSource === 'moxfield'}>
            <RadioInput
              type="radio"
              name="csvSource"
              checked={selectedSource === 'moxfield'}
              onChange={() => setSelectedSource('moxfield')}
            />
            Moxfield
          </SourceOption>
        </SourceOptions>
      </FormGroup>

      {selectedSource === 'moxfield' ? (
        <FormGroup>
          <Label>Lim inn kortliste (Copy for Moxfield-format)</Label>
          <MoxfieldTextarea
            value={moxfieldTekst}
            onChange={e => setMoxfieldTekst(e.target.value)}
            placeholder={"1 Sol Ring (C21) 263\n4 Counterspell (A25) 50\n2 Lightning Bolt (M11) 149 *F*"}
          />
        </FormGroup>
      ) : (
        <FormGroup>
          <Label>Velg CSV-fil</Label>
          <FileInputLabel>
            Velg fil
            <FileInput
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
          </FileInputLabel>
          {selectedFile && (
            <FileName>{selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</FileName>
          )}
        </FormGroup>
      )}

      {error && (
        <Alert variant="danger">
          {error}
          <div style={{ marginTop: theme.spacing.sm }}>
            <Button variant="outline" size="small" onClick={() => setError(null)}>
              Prøv igjen
            </Button>
          </div>
        </Alert>
      )}

      <Button
        variant="accent"
        size="large"
        fullWidth
        onClick={handleUpload}
        disabled={
          !selectedSource ||
          (selectedSource === 'moxfield' ? !moxfieldTekst.trim() : !selectedFile) ||
          loading
        }
      >
        {loading ? 'Søker etter treff...' : 'Finn treff i kjøpslisten'}
      </Button>
    </UploadCard>
  );

  // ---- Results ----
  const renderResults = () => {
    if (matchedItems.length === 0) {
      return (
        <>
          <Alert variant="info">
            Ingen treff funnet i vår kjøpsliste for den opplastede filen.
          </Alert>
          <Button variant="outline" onClick={handleReset}>
            Ny opplasting
          </Button>
        </>
      );
    }

    const totalQuantity = matchedItems.reduce((sum, item) => sum + (quantities[item.buylistID] || 1), 0);
    const totalCashValue = matchedItems.reduce((sum, item) => sum + (item.buyPrice || 0) * (quantities[item.buylistID] || 1), 0);
    const totalCreditValue = matchedItems.reduce((sum, item) => sum + (item.buylistStoreCreditPrice || 0) * (quantities[item.buylistID] || 1), 0);

    return (
      <>
        <ResultsSummary>
          <ResultsCount>Fant {matchedItems.length} kort i vår kjøpsliste</ResultsCount>
          <ButtonGroup>
            <Button variant="success" onClick={handleAddAll}>
              Legg til alle i handlekurv
            </Button>
            <Button variant="outline" onClick={handleReset}>
              Ny opplasting
            </Button>
          </ButtonGroup>
        </ResultsSummary>

        <SummaryBox>
          <SummaryTitle>Sammendrag</SummaryTitle>
          <SummaryRow>
            <span>Antall unike kort:</span>
            <span>{matchedItems.length}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Totalt antall:</span>
            <span>{totalQuantity}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Total kontantverdi:</span>
            <span>{totalCashValue.toFixed(2)} kr</span>
          </SummaryRow>
          <StoreCreditHighlight>
            <span>Total butikkreditt-verdi:</span>
            <span>{totalCreditValue.toFixed(2)} kr</span>
          </StoreCreditHighlight>
        </SummaryBox>

        <TableWrapper>
          <ResultsTable>
            <THead>
              <tr>
                <th>Bilde</th>
                <th>Kortnavn</th>
                <th>Utgave</th>
                <th>Foil</th>
                <th>Maks</th>
                <th>Kontantpris</th>
                <th>Kredittpris</th>
                <th>Antall</th>
                <th></th>
                <th></th>
              </tr>
            </THead>
            <TBody>
              {matchedItems.map(item => (
                <tr key={item.buylistID}>
                  <td>
                    <ItemImage $imageUrl={getBuylistImageUrl(item.cardBlueprintId, item.image_url)} />
                  </td>
                  <td>{item.blueprintName}</td>
                  <td>{item.expansionName}</td>
                  <td>{item.foil ? 'Ja' : 'Nei'}</td>
                  <td>{item.maxQuantity}</td>
                  <td>
                    <PriceCell>{(item.buyPrice || 0).toFixed(2)} kr</PriceCell>
                  </td>
                  <td>
                    <PriceCell>{(item.buylistStoreCreditPrice || 0).toFixed(2)} kr</PriceCell>
                  </td>
                  <td>
                    <QuantityInput
                      type="number"
                      min={1}
                      max={item.maxQuantity}
                      value={quantities[item.buylistID] || 1}
                      onChange={e => handleQuantityChange(item.buylistID, parseInt(e.target.value) || 1, item.maxQuantity)}
                    />
                  </td>
                  <td>
                    <Button variant="primary" size="small" onClick={() => handleAddItem(item)}>
                      Legg til
                    </Button>
                  </td>
                  <td>
                    <RemoveButton onClick={() => handleRemoveItem(item.buylistID)} title="Fjern fra listen">
                      ✕
                    </RemoveButton>
                  </td>
                </tr>
              ))}
            </TBody>
          </ResultsTable>
        </TableWrapper>

        <ButtonGroup>
          <Button variant="success" onClick={handleAddAll}>
            Legg til alle i handlekurv
          </Button>
        </ButtonGroup>
      </>
    );
  };

  return (
    <BuylistLayout>
      <Helmet>
        <title>Last opp kortliste – Selg MTG-kort i bulk | Tryllepapp</title>
        <meta name="description" content="Selg mange Magic-kort på én gang: last opp en CSV-fil fra Manabox eller Deckbox, eller lim inn en Moxfield-liste, og få pris på hele listen automatisk." />
        <link rel="canonical" href="https://selg.tryllepapp.no/csv-opplasting" />
        <meta property="og:title" content="Last opp kortliste – Selg MTG-kort i bulk | Tryllepapp" />
        <meta property="og:description" content="Last opp CSV fra Manabox eller Deckbox, eller lim inn en Moxfield-liste, og få pris på hele listen automatisk." />
        <meta property="og:url" content="https://selg.tryllepapp.no/csv-opplasting" />
      </Helmet>
      <PageContainer>
        <PageTitle>Last opp kortliste</PageTitle>
        {!uploadComplete ? renderUploadForm() : renderResults()}
      </PageContainer>
    </BuylistLayout>
  );
};

export default CsvUploadPage;
